import { Checkbox, Col, Form, Radio, Row, Spin, type FormInstance } from "antd";
import React, { useEffect } from "react";
import Text from "../../../components/Text/Text";
import Input from "../../../components/Input/Input";
import DatePicker, { TimePicker } from "../../../components/DatePicker/DatePicker";
import dayjs, { type Dayjs } from "dayjs";
import Select from "../../../components/Select/Select";
import { QUOTA_TYPE } from "../../../definitions/QuotaType";
import { color, icon } from "../../../resource";
import { CalendarOutlined, ClockCircleOutlined, MinusCircleFilled } from "@ant-design/icons";
import { handleNumberOnlyCopyPaste } from "../../../utility/validator";
import CardConditionQuota from "../../../components/Card/CardConditionQuota";
import { numberFormatter } from "../../../utility/Formatter";

interface StepOneDupProps {
  form1: FormInstance;
  form2: FormInstance;
  isEditing?: boolean;
  isDuplicate?: boolean;
  loadingStep?: boolean;
  setLoadingStep?: any;
}

const StepOneDup = ({
  form1,
  form2,
  isEditing,
  isDuplicate,
  loadingStep,
  setLoadingStep,
}: StepOneDupProps) => {
  const userCompany = JSON.parse(localStorage.getItem("company")!);
  const quotaType = Form.useWatch("quotaType", form1);
  const limitOrderDay = Form.useWatch("limitOrderDay", form1);
  const limitOrderPerDayType = Form.useWatch("limitOrderPerDayType", form1);
  const quotaShopPrice = Form.useWatch("quotaShopPrice", form1);

  const dataForm1 = form1.getFieldsValue(true);
  const isStarting =
    dataForm1.startDate <= dayjs().startOf("day") && dataForm1.endDate >= dayjs().startOf("day");
  const dataForm2 = form2.getFieldsValue(true);

  useEffect(() => {
    setTimeout(() => {
      setLoadingStep(false);
    }, 300);
  }, [loadingStep]);

  return (
    <>
      {loadingStep ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "40vh",
          }}
        >
          <Spin size='large' />
        </div>
      ) : (
        <Form
          form={form1}
          layout='vertical'
          initialValues={{
            startDate: null,
            endDate: null,
            perShop: false,
          }}
        >
          <>
            <Text fontSize={16} fontWeight={700}>
              รายละเอียดโควตา
            </Text>
            <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
              <Col span={12}>
                <Form.Item
                  label={"ชื่อโควตา"}
                  name='quotaName'
                  rules={[{ required: true, message: "โปรดระบุชื่อโควตา" }]}
                  required
                >
                  <Input placeholder='ระบุชื่อโควตา' autoComplete='off' />
                </Form.Item>
              </Col>
              <Col span={12} />
            </Row>
            <Row align='middle' gutter={16}>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name='startDate'
                      label='วันที่เริ่มโควตา'
                      rules={[
                        {
                          required: true,
                          message: "*โปรดเลือกวันที่เริ่มต้น",
                        },
                      ]}
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        enablePast
                        disabledDate={(current: Dayjs) => {
                          const endDate = form1?.getFieldValue("endDate");
                          if (endDate) {
                            return current && current?.isAfter(dayjs(endDate));
                          }
                          return false;
                        }}
                        suffixIcon={<CalendarOutlined />}
                        disabled={isEditing && isStarting}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name='startTime'
                      label='เวลาเริ่มต้น'
                      initialValue={dayjs("00:00", "HH:mm")}
                    >
                      <TimePicker
                        allowClear={false}
                        suffixIcon={<ClockCircleOutlined />}
                        disabled={isEditing && isStarting}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name='endDate'
                      label='วันที่สิ้นสุดโควตา'
                      rules={[
                        {
                          required: true,
                          message: "*โปรดเลือกวันที่สิ้นสุด",
                        },
                      ]}
                    >
                      <DatePicker
                        suffixIcon={<CalendarOutlined />}
                        style={{ width: "100%" }}
                        enablePast
                        disabledDate={(current: Dayjs) => {
                          const startDate = form1?.getFieldValue("startDate");
                          return current && current?.isBefore(dayjs(startDate));
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name='endTime'
                      label='เวลาสิ้นสุดโควตา'
                      initialValue={dayjs("23:59", "HH:mm")}
                    >
                      <TimePicker
                        suffixIcon={<ClockCircleOutlined />}
                        allowClear={false}
                        disabledTime={(now) => {
                          const startDate = dayjs(form1?.getFieldValue("startDate"));
                          const endDate = dayjs(form1?.getFieldValue("endDate"));
                          const startTime = dayjs(form1?.getFieldValue("startTime"));
                          const isSameDay =
                            startDate.isValid() &&
                            endDate.isValid() &&
                            startDate.isSame(endDate, "day");
                          if (!isSameDay || !startTime.isValid()) return {};
                          const hour = startTime.hour();
                          const minute = startTime.minute();
                          const hours: number[] = [];
                          const minutes: number[] = [];
                          for (let i = 0; i < hour; i++) {
                            hours.push(i);
                          }
                          for (let i = 0; i <= minute; i++) {
                            minutes.push(i);
                          }
                          return {
                            disabledHours: () => hours,
                            disabledMinutes: (selectedHour: number) =>
                              selectedHour === hour ? minutes : [],
                          };
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
              <Col span={12}>
                <img
                  src={icon.productQuota_inactive}
                  style={{ width: "22px", padding: "8px 0px 8px 0px", marginRight: 8 }}
                />
                <Text fontSize={16} fontWeight={700}>
                  รูปแบบโควตา&nbsp;<span style={{ color: color.error }}>*</span>
                </Text>
                <Form.Item
                  style={{ marginTop: 12 }}
                  name='quotaType'
                  rules={[
                    {
                      required: true,
                      message: "*โปรดเลือกรูปแบบโควตา",
                    },
                  ]}
                >
                  <Select
                    style={{ marginBottom: 8 }}
                    data={QUOTA_TYPE}
                    placeholder='กรุณาเลือกรูปแบบโควตา'
                    onChange={() => form2.resetFields()}
                    disabled={isEditing || isDuplicate}
                  />
                </Form.Item>
              </Col>
              {quotaType === "QUOTA_TOTAL" && (
                <Col span={6} style={{ marginTop: 29 }}>
                  <Form.Item
                    label={"จำนวนโควตารวม"}
                    name='quotaTotalAmount'
                    rules={[
                      { required: true, message: "โปรดระบุจำนวนโควตารวม" },
                      {
                        validator: (_, value) => {
                          const numericValue = parseFloat(value);
                          if (isEditing) {
                            const quotaUsed =
                              Object.keys(dataForm2).length > 0
                                ? dataForm2?.summary[0]?.quotaUsed || 0
                                : 0;
                            if (numericValue < quotaUsed) {
                              return Promise.reject(`โปรดระบุโควตาให้มากกว่าที่ใช้ไปแล้ว`);
                            }
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    required
                  >
                    <Input
                      placeholder='ระบุจำนวน'
                      autoComplete='off'
                      onKeyDown={handleNumberOnlyCopyPaste}
                      suffix={
                        <>
                          {userCompany?.companyType === "Chemical" ? (
                            <Text fontFamily='Sarabun' fontSize={14}>
                              ลัง/กระสอบ
                            </Text>
                          ) : (
                            <Text fontFamily='Sarabun' fontSize={14}>
                              ตัน
                            </Text>
                          )}
                        </>
                      }
                    />
                  </Form.Item>
                </Col>
              )}
              {isEditing &&
                Object.keys(dataForm2)?.length > 0 &&
                dataForm2?.summary &&
                dataForm2?.summary[0]?.quotaUsed > 0 && (
                  <Col style={{ marginTop: "60px" }}>
                    <Text fontFamily='Sarabun' fontSize={14}>
                      ใช้แล้ว&nbsp;
                      <Text fontSize={14} color='warning' fontWeight={600}>
                        {numberFormatter(dataForm2?.summary[0]?.quotaUsed || 0, 2)}
                      </Text>{" "}
                      / {numberFormatter(dataForm2?.summary[0]?.quotaReceive || 0, 2)}{" "}
                      {userCompany?.companyType === "Chemical" ? "ลัง/กระสอบ" : "ตัน"}
                    </Text>
                  </Col>
                )}
            </Row>
            <div style={{ marginTop: 32 }}>
              <Text level={5} fontWeight={700}>
                เงื่อนไขร้านค้า
              </Text>
              <Row style={{ marginTop: 14 }} gutter={16}>
                <Col span={12}>
                  <CardConditionQuota title='ร้านค้าที่เข้าร่วมโควตา' icon={icon.ShopQuota}>
                    <Row gutter={[16, 16]}>
                      <Col span={6}>
                        <Text fontFamily='Sarabun' fontSize={14}>
                          ราคาสินค้า<span style={{ color: color.error }}>*</span>
                        </Text>
                      </Col>
                      <Col span={18}>
                        <Form.Item
                          name='quotaShopPrice'
                          rules={[
                            {
                              required: true,
                              message: "*โปรดเลือกราคาสินค้า",
                            },
                          ]}
                        >
                          <Radio.Group
                            onChange={(e) => {
                              if (e.target.value === "NORMAL_PRICE") {
                                form1.setFieldValue("quotaShopPriceCondition", "DENY_ORDER");
                                let mapData = [];
                                const f2 = form2?.getFieldsValue(true);
                                if (Object.keys(dataForm2)?.length > 0) {
                                  if (form1.getFieldValue("quotaType") !== "QUOTA_GROUP") {
                                    mapData = f2.products?.map((product: any, index: number) => {
                                      form2?.setFieldValue(
                                        `specialPrice-${product.productId}`,
                                        undefined,
                                      );
                                      return {
                                        ...product,
                                        discount: undefined,
                                      };
                                    });
                                  } else {
                                    mapData = f2.products?.map((product: any, index: number) => {
                                      return {
                                        ...product,
                                        productList: product.productList.map((item: any) => {
                                          form2?.setFieldValue(
                                            `specialPrice-${item.productId}`,
                                            undefined,
                                          );
                                          return {
                                            ...item,
                                            discount: undefined,
                                          };
                                        }),
                                      };
                                    });
                                  }
                                  form2.setFieldValue("products", mapData);
                                }
                              } else {
                                form1.setFieldValue("quotaShopPriceCondition", undefined);
                              }
                            }}
                            style={{ display: "flex", flexDirection: "column" }}
                            disabled={isEditing && isStarting}
                          >
                            <Radio value='NORMAL_PRICE'>
                              <Text fontFamily='Sarabun' fontSize={14}>
                                ราคาปกติ
                              </Text>
                            </Radio>

                            <Radio value='SPECIAL_PRICE'>
                              <Text fontFamily='Sarabun' fontSize={14}>
                                ราคาพิเศษ
                              </Text>
                            </Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col span={6}>
                        <Text fontFamily='Sarabun' fontSize={14}>
                          การสั่งซื้อ<span style={{ color: color.error }}>*</span>
                        </Text>
                      </Col>
                      <Col span={18}>
                        <Form.Item
                          name='quotaShopPriceCondition'
                          rules={[
                            {
                              required: true,
                              message: "*โปรดเลือกรูปแบบสั่งซื้อ",
                            },
                          ]}
                        >
                          <Radio.Group style={{ display: "flex", flexDirection: "column" }}>
                            <Radio
                              value='CONTINUE_NORMAL_PRICE'
                              disabled={quotaShopPrice === "NORMAL_PRICE"}
                            >
                              <Text fontFamily='Sarabun' fontSize={14}>
                                เมื่อใช้โควตาครบ สามารถสั่งซื้อสินค้าเดิมต่อได้ในราคาปกติ
                              </Text>
                            </Radio>
                            <Radio value='DENY_ORDER'>
                              <Text fontFamily='Sarabun' fontSize={14}>
                                เมื่อใช้โควตาครบ ไม่สามารถสั่งซื้อสินค้าเดิมต่อได้
                              </Text>
                            </Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                  </CardConditionQuota>
                </Col>
                <Col span={12}>
                  <CardConditionQuota
                    title='ร้านค้าที่ไม่ได้เข้าร่วมโควตา'
                    icon={<MinusCircleFilled style={{ color: color.Disable, fontSize: 16 }} />}
                  >
                    <Row gutter={[16, 16]}>
                      <Col span={6}>
                        <Text fontFamily='Sarabun' fontSize={14}>
                          การสั่งซื้อ<span style={{ color: color.error }}>*</span>
                        </Text>
                      </Col>
                      <Col span={18}>
                        <Form.Item
                          name='nonQuotaShopCondition'
                          rules={[
                            {
                              required: true,
                              message: "*โปรดเลือกการสั่งซื้อ",
                            },
                          ]}
                        >
                          <Radio.Group style={{ display: "flex", flexDirection: "column" }}>
                            <Radio value='CONTINUE_NORMAL_PRICE'>
                              <Text fontFamily='Sarabun' fontSize={14}>
                                สามารถสั่งซื้อสินค้าเดียวกับร้านที่เข้าร่วมได้ในราคาปกติ
                              </Text>
                            </Radio>

                            <Radio value='DENY_ORDER'>
                              <Text fontFamily='Sarabun' fontSize={14}>
                                ไม่สามารถสั่งซื้อได้ทุกสินค้า
                              </Text>
                            </Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                  </CardConditionQuota>
                </Col>
              </Row>
            </div>
            <div style={{ marginTop: 32 }}>
              <Text level={5} fontWeight={700}>
                เงื่อนไขเพิ่มเติม
              </Text>
              <div style={{ marginTop: 12 }}>
                <Form.Item noStyle name='limitQuantityOrder' valuePropName='checked'>
                  <Checkbox
                    style={{ width: "max-content", marginBottom: "6px" }}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        const f2 = form2?.getFieldsValue(true);
                        let mapData = [];
                        if (Object.keys(dataForm2)?.length > 0) {
                          if (form1.getFieldValue("quotaType") !== "QUOTA_GROUP") {
                            mapData = f2?.products?.map((product: any, index: number) => {
                              form2?.setFieldValue(`limitQty-${product.productId}`, undefined);
                              return {
                                ...product,
                                orderLimit: undefined,
                              };
                            });
                          } else {
                            mapData = f2?.products?.map((product: any, index: number) => {
                              return {
                                ...product,
                                productList: product.productList.map((item: any) => {
                                  form2.setFieldValue(`limitQty-${item.productId}`, undefined);
                                  return {
                                    ...item,
                                    orderLimit: undefined,
                                  };
                                }),
                              };
                            });
                          }
                          form2.setFieldValue("products", mapData);
                        }
                      }
                      form1.setFieldValue("limitQuantityOrder", e.target.checked);
                    }}
                    disabled={isEditing && isStarting}
                  >
                    <Text fontFamily='Sarabun' fontSize={14}>
                      จำกัดจำนวนซื้อรายสินค้า / คำสั่งซื้อ
                    </Text>
                  </Checkbox>
                </Form.Item>
                <Form.Item noStyle name='limitOrderDay' valuePropName='checked'>
                  <Checkbox
                    style={{ marginLeft: 24, width: "max-content" }}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        form1.setFieldValue("countOrder", undefined);
                        form1.setFieldValue("limitOrderPerDayType", undefined);
                        form1.setFieldValue("perShop", false);
                        form1.setFieldValue("allShop", undefined);
                      }
                      form1.setFieldValue("limitOrderDay", e.target.checked);
                    }}
                    disabled={isEditing && isStarting}
                  >
                    <Text fontFamily='Sarabun' fontSize={14}>
                      จำกัดการสั่งซื้อ / วัน
                    </Text>
                  </Checkbox>
                </Form.Item>
                {limitOrderDay && (
                  <Form.Item noStyle name='limitOrderPerDayType'>
                    <Radio.Group
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        margin: "8px 0px 12px 18px",
                      }}
                      onChange={(e) => {
                        const f2 = form2.getFieldsValue(true);
                        if (Object.keys(dataForm2)?.length > 0) {
                          if (e.target.value === "perShop") {
                            let mapData = [];
                            if (form1.getFieldValue("quotaType") === "QUOTA_INDIVIDUAL") {
                              mapData = f2?.products?.map((product: any, index: number) => {
                                return {
                                  ...product,
                                  storeList: product.storeList?.map((store: any) => {
                                    form2.setFieldValue(
                                      `orderPerDay-${store.customerCompanyId}-${index}`,
                                      undefined,
                                    );
                                    return {
                                      ...store,
                                      orderPerDay: undefined,
                                    };
                                  }),
                                };
                              });
                            } else if (form1.getFieldValue("quotaType") === "QUOTA_GROUP") {
                              mapData = f2?.products?.map((product: any, index: number) => {
                                return {
                                  ...product,
                                  storeList: product.storeList?.map((store: any) => {
                                    form2.setFieldValue(
                                      `orderPerDay-${store.customerCompanyId}-${product.groupNo}`,
                                      undefined,
                                    );
                                    return {
                                      ...store,
                                      orderPerDay: undefined,
                                    };
                                  }),
                                };
                              });
                            } else {
                              const mapStores = f2?.stores?.map((shop: any, index: number) => {
                                form2.setFieldValue(
                                  `orderPerDay-${shop.customerCompanyId}`,
                                  undefined,
                                );
                                return {
                                  ...shop,
                                  orderPerDay: undefined,
                                };
                              });
                              form2.setFieldValue("stores", mapStores);
                            }
                            form1.setFieldValue("countOrder", undefined);
                          }
                        }
                        form1.setFieldValue("limitOrderPerDayType", e.target.value);
                      }}
                    >
                      <Radio value='allShop' disabled={isEditing && isStarting}>
                        <Text fontFamily='Sarabun' fontSize={14}>
                          ทุกร้านเท่ากัน
                        </Text>
                      </Radio>
                      {limitOrderPerDayType === "allShop" && (
                        <Form.Item
                          name='countOrder'
                          rules={[{ required: true, message: "*โปรดระบุจำนวน" }]}
                          style={{ marginLeft: 24 }}
                        >
                          <Input
                            autoComplete='off'
                            onKeyDown={handleNumberOnlyCopyPaste}
                            placeholder='ระบุจำนวน'
                            style={{ width: "20%", marginTop: 12 }}
                            suffix={
                              <Text fontFamily='Sarabun' fontSize={14}>
                                ครั้ง / วัน
                              </Text>
                            }
                          />
                        </Form.Item>
                      )}
                      <Radio value='perShop' disabled={isEditing && isStarting}>
                        <Text fontFamily='Sarabun' fontSize={14}>
                          รายร้าน
                        </Text>
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                )}
              </div>
            </div>
          </>
        </Form>
      )}
    </>
  );
};

export default StepOneDup;
